import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { ToastrService } from 'ngx-toastr';
import { LocalCartService } from 'src/app/nio-common/services/local-cart.service';
import { SharedSessionService } from 'src/app/nio-common/services/shared-session.service';
import { GuestCustomerInfo } from '../../models/cart.model';
import { GuestCheckoutService } from '../../services/guest-checkout.service';

@Component({
  selector: 'app-modal-customer-info',
  templateUrl: './modal-customer-info.component.html',
  styleUrls: ['./modal-customer-info.component.scss'],
})
export class ModalCustomerInfoComponent {
  @Input() totalPrice = 0;
  @Input() itemCount = 0;

  customerForm: FormGroup;
  isProcessingCheckout = false;

  // ngx-intl-tel-input configuration
  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.Malaysia,
    CountryISO.Singapore,
    CountryISO.UnitedStates,
  ];

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly guestCheckoutService: GuestCheckoutService,
    private readonly localCartService: LocalCartService,
    private readonly sharedSessionService: SharedSessionService,
  ) {
    // Initialize customer form
    this.customerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [undefined, [Validators.required]],
    });

    // If user is logged in, prefill email and name, and disable email field
    if (this.isLoggedIn) {
      this.customerForm.patchValue({
        email: this.sharedSessionService.userEmail,
        name: this.sharedSessionService.userName,
      });
      this.customerForm.get('email')?.disable();
    }
  }

  // Getter to check if user is logged in
  get isLoggedIn(): boolean {
    return this.sharedSessionService.isLoggedIn;
  }

  // Form getters for validation
  get name() {
    return this.customerForm.get('name');
  }
  get email() {
    return this.customerForm.get('email');
  }
  get phone() {
    return this.customerForm.get('phone');
  }

  close(): void {
    this.modal.dismiss();
  }

  async onSubmit(): Promise<void> {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      this.toastrService.error('Please fill in all required fields correctly', 'Form Error');
      return;
    }

    this.isProcessingCheckout = true;

    try {
      const photoIds = this.localCartService.getPhotoIds();
      const phoneValue = this.customerForm.value.phone;

      const customerInfo: GuestCustomerInfo = {
        name: this.customerForm.value.name,
        email: this.isLoggedIn
          ? this.sharedSessionService.userEmail
          : this.customerForm.value.email,
        // phone: phoneValue?.internationalNumber || phoneValue?.e164Number || undefined,
        phone: phoneValue?.e164Number || undefined,
      };

      const checkoutResponse = await this.guestCheckoutService.guestCheckout(
        photoIds,
        customerInfo,
      );

      if (checkoutResponse?.url) {
        // Clear cart after successful checkout initiation
        this.localCartService.clearCart();

        // Close modal and redirect to payment
        this.modal.close({ success: true, paymentUrl: checkoutResponse.url });
      } else {
        this.toastrService.error('Failed to initiate checkout', 'Error');
      }
    } catch (error) {
      console.error('Guest checkout error:', error);
      this.toastrService.error('Failed to process checkout', 'Error');
    } finally {
      this.isProcessingCheckout = false;
    }
  }
}
